import { reactive, computed } from 'vue'
import store from '@/store'
import eventbus from '@/plugins/eventbus'
import importsAPI from '@/api/imports'

// CONFIG is defined globally in vite.config.js
/* global CONFIG */

class ImportProgressWebSocket {
  constructor() {
    this.ws = null
    this.reconnectTimer = null
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectDelay = 1000
    this.pingInterval = null
    this.pingIntervalTime = 30000 // 30 seconds
    this.stuckImportTimeout = 3 * 60 * 1000 // 3 minutes
    this.stuckCheckInterval = null
    this.stuckCheckIntervalTime = 60000 // Check every minute
    this.connectionPromise = null // Track ongoing connection attempts
    this.connectionState = 'disconnected' // disconnected, connecting, connected, reconnecting

    // Reactive state for Vue 2 compatibility
    this.state = reactive({
      connected: false,
      connecting: false,
      imports: {}, // Map of importId -> progress data
      error: null
    })
  }

  async connect() {
    console.log('[ImportWS] Connect called, current state:', {
      wsReadyState: this.ws?.readyState,
      connectionState: this.connectionState,
      connecting: this.state.connecting,
      connected: this.state.connected
    })

    // If already connected, return success
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[ImportWS] Already connected')
      return Promise.resolve(true)
    }

    // If a connection attempt is in progress, return the existing promise
    if (this.connectionPromise) {
      console.log('[ImportWS] Connection attempt already in progress, returning existing promise')
      return this.connectionPromise
    }

    // Start a new connection attempt
    this.connectionPromise = this._attemptConnection()
      .then(result => {
        this.connectionPromise = null
        return result
      })
      .catch(error => {
        this.connectionPromise = null
        throw error
      })

    return this.connectionPromise
  }

  async _attemptConnection() {
    this.connectionState = 'connecting'
    this.state.connecting = true
    this.state.error = null

    const token = store.getters['auth/accessToken']
    if (!token) {
      console.error('[ImportWS] No authentication token available')
      this.state.error = 'No authentication token'
      this.state.connecting = false
      this.connectionState = 'disconnected'
      throw new Error('No authentication token')
    }

    // Determine WebSocket URL based on environment
    // CRITICAL FIX: For extensions, derive protocol from API URL, not window.location
    // Extensions use safari-extension:// or chrome-extension:// protocols
    const apiUrl = CONFIG.apiUrl || 'http://localhost:3000/v1'

    // Check if API uses HTTPS to determine WebSocket protocol
    const apiUsesHttps = apiUrl.startsWith('https://')
    const wsProtocol = apiUsesHttps ? 'wss:' : 'ws:'

    console.log('[ImportWS] WebSocket protocol determination:', {
      apiUrl,
      apiUsesHttps,
      selectedProtocol: wsProtocol,
      windowProtocol: window.location.protocol,
      buildTarget: BUILD_TARGET
    })

    // Convert API URL to WebSocket URL
    let wsBaseUrl = apiUrl.replace(/^https?:/, wsProtocol)
    if (!wsBaseUrl.endsWith('/v1')) {
      wsBaseUrl = `${wsBaseUrl}/v1`
    }

    const wsUrl = `${wsBaseUrl}/imports/ws?token=${encodeURIComponent(token)}`
    console.log('[ImportWS] Connecting to:', wsUrl.replace(/token=.*/, 'token=***'))

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(wsUrl)

        const connectionTimeout = setTimeout(() => {
          console.error('[ImportWS] Connection timeout')
          this.ws?.close()
          this.state.error = 'Connection timeout'
          this.state.connecting = false
          this.connectionState = 'disconnected'
          reject(new Error('Connection timeout'))
        }, 10000) // 10 second timeout

        // Setup handlers with promise resolution
        this.ws.onopen = () => {
          clearTimeout(connectionTimeout)
          console.log('[ImportWS] Connected successfully')
          this.connectionState = 'connected'
          this.state.connected = true
          this.state.connecting = false
          this.reconnectAttempts = 0

          // Start ping interval
          this.startPingInterval()

          // Start stuck import detection
          this.startStuckImportDetection()

          eventbus.emit('import:ws:connected')
          resolve(true)
        }

        this.ws.onerror = (error) => {
          clearTimeout(connectionTimeout)
          console.error('[ImportWS] WebSocket error:', error)
          this.state.error = 'WebSocket connection error'
          this.state.connecting = false
          this.connectionState = 'disconnected'
          eventbus.emit('import:ws:error', error)
          reject(error)
        }

        // Setup other event handlers
        this.setupEventHandlers()
      } catch (error) {
        console.error('[ImportWS] Failed to create WebSocket:', error)
        this.state.error = 'Failed to create WebSocket connection'
        this.state.connecting = false
        this.connectionState = 'disconnected'
        reject(error)
      }
    })
  }

  setupEventHandlers() {
    if (!this.ws) return

    // Note: onopen and onerror are set in _attemptConnection for promise handling
    // Here we only set up onmessage and onclose

    this.ws.onmessage = event => {
      try {
        const message = JSON.parse(event.data)
        this.handleMessage(message)
      } catch (error) {
        console.error('[ImportWS] Error parsing message:', error)
      }
    }

    this.ws.onclose = (event) => {
      console.log('[ImportWS] Disconnected, code:', event.code, 'reason:', event.reason)
      this.connectionState = 'disconnected'
      this.state.connected = false
      this.state.connecting = false
      this.stopPingInterval()
      this.stopStuckImportDetection()

      eventbus.emit('import:ws:disconnected')

      // Attempt to reconnect if we have active imports
      const terminalStates = ['completed', 'failed', 'cancelled', 'timeout']
      const hasActiveImports = Object.keys(this.state.imports).some(importId => {
        const imp = this.state.imports[importId]
        return imp && !terminalStates.includes(imp.status)
      })

      if (hasActiveImports && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.connectionState = 'reconnecting'
        this.scheduleReconnect()
      }
    }
  }

  handleMessage(message) {
    console.log('[ImportWS] Received message:', message.type, message)

    switch (message.type) {
      case 'connected':
        console.log('[ImportWS] Authenticated for user:', message.userId)
        break

      case 'import:progress':
        this.updateImportProgress(message.importId, message.data, message.timestamp)
        break

      case 'pong':
        // Keep-alive response
        break

      case 'error':
        console.error('[ImportWS] Error message:', message.message)
        this.state.error = message.message
        eventbus.emit('import:ws:error', { message: message.message })
        break

      default:
        console.warn('[ImportWS] Unknown message type:', message.type)
    }
  }

  updateImportProgress(importId, progressData, timestamp) {
    console.log('[ImportWS] Progress update for import:', importId, {
      status: progressData.status,
      progressPercentage: progressData.progressPercentage,
      processed: progressData.bookmarksProcessed,
      total: progressData.totalBookmarks,
      failed: progressData.bookmarksFailed
    })

    const derived = progressData.derived || {}
    const totalBookmarks = Number(progressData.totalBookmarks || 0)
    const processed = Number(progressData.bookmarksProcessed || 0)
    const failed = Number(progressData.bookmarksFailed || 0)
    const attemptedCandidate = Number(derived.attempted)
    const attemptedFromPayload = Number.isFinite(attemptedCandidate) ? attemptedCandidate : processed + failed
    const attempted = Math.max(0, attemptedFromPayload)
    const pendingCandidate = Number(derived.pending)
    const pendingFromPayload = Number.isFinite(pendingCandidate)
      ? pendingCandidate
      : Math.max(0, totalBookmarks - attempted)
    const pending = Math.max(0, pendingFromPayload)
    const normalizedCandidate = Number(derived.normalizedProgressPercentage)
    const percentageFromPayload = Number.isFinite(normalizedCandidate)
      ? normalizedCandidate
      : Number(progressData.progressPercentage)
    const normalizedPercentage =
      Number.isFinite(percentageFromPayload)
        ? percentageFromPayload
        : totalBookmarks > 0
          ? Math.round((attempted / totalBookmarks) * 100)
          : 0
    const lastServerMarker = progressData.lastProcessedAt || progressData.completedAt || timestamp
    const lastServerUpdate = lastServerMarker ? new Date(lastServerMarker) : new Date()
    const receivedAt = timestamp ? new Date(timestamp) : new Date()

    const stateEntry = {
      ...progressData,
      progressPercentage: normalizedPercentage,
      importId,
      attempted,
      pending,
      lastUpdated: lastServerUpdate,
      lastServerUpdate,
      receivedAt
    }

    // Update reactive state
    this.state.imports[importId] = stateEntry

    // Calculate total queue size across all active imports
    let totalQueueSize = 0
    let totalAttempted = 0
    let aggregateTotalBookmarks = 0

    Object.values(this.state.imports).forEach(imp => {
      if (imp.status === 'processing' || imp.status === 'pending') {
        const impTotal = Number(imp.totalBookmarks || 0)
        const impAttemptedCandidate = Number(imp.attempted)
        const impAttempted = Number.isFinite(impAttemptedCandidate)
          ? impAttemptedCandidate
          : Number(imp.bookmarksProcessed || 0) + Number(imp.bookmarksFailed || 0)
        const impPendingCandidate = Number(imp.pending)
        const impPending = Number.isFinite(impPendingCandidate)
          ? impPendingCandidate
          : Math.max(0, impTotal - impAttempted)

        totalQueueSize += Math.max(0, impPending)
        totalAttempted += Math.max(0, impAttempted)
        aggregateTotalBookmarks += impTotal
      }
    })

    // Update Vuex store for backward compatibility
    store.commit('collectionItems/setImportQueueSize', totalQueueSize)

    if (progressData.totalBookmarks) {
      store.commit('collectionItems/setLastImportSize', progressData.totalBookmarks)
    }

    // Emit events for UI updates
    if (['completed', 'failed', 'cancelled', 'timeout'].includes(progressData.status)) {
      // Import finished
      console.log('[ImportWS] Import', importId, 'finished with status:', progressData.status)

      // Update store to trigger UI refresh
      store.dispatch('collectionItems/getStats').catch(console.error)

      // Emit completion event
      eventbus.emit('import:completed', {
        importId,
        status: stateEntry.status,
        totalBookmarks: stateEntry.totalBookmarks,
        bookmarksProcessed: stateEntry.bookmarksProcessed,
        bookmarksFailed: stateEntry.bookmarksFailed,
        progressPercentage: stateEntry.progressPercentage,
        attempted: stateEntry.attempted,
        pending: stateEntry.pending,
        lastUpdated: stateEntry.lastUpdated,
        progress: stateEntry
      })

      // Clean up after a delay
      setTimeout(() => {
        delete this.state.imports[importId]

        // Disconnect if no more active imports
        const hasActiveImports = Object.keys(this.state.imports).some(id => {
          const imp = this.state.imports[id]
          return imp && !['completed', 'failed', 'cancelled', 'timeout'].includes(imp.status)
        })

        if (!hasActiveImports) {
          console.log('[ImportWS] No active imports, disconnecting')
          this.disconnect()
        }
      }, 5000)
    } else {
      // Import in progress
      eventbus.emit('import:progress', {
        importId,
        progress: stateEntry,
        totalQueueSize,
        totalProcessed: totalAttempted,
        totalAttempted,
        totalBookmarks: aggregateTotalBookmarks
      })
    }
  }

  startPingInterval() {
    this.stopPingInterval()
    this.pingInterval = setInterval(() => {
      this.sendPing()
    }, this.pingIntervalTime)
  }

  stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval)
      this.pingInterval = null
    }
  }

  startStuckImportDetection() {
    this.stopStuckImportDetection()
    this.stuckCheckInterval = setInterval(() => {
      this.checkForStuckImports().catch(error => {
        console.error('[ImportWS] Stuck import check failed:', error)
      })
    }, this.stuckCheckIntervalTime)
  }

  stopStuckImportDetection() {
    if (this.stuckCheckInterval) {
      clearInterval(this.stuckCheckInterval)
      this.stuckCheckInterval = null
    }
  }

  async checkForStuckImports() {
    const now = Date.now()

    for (const [importId, imp] of Object.entries(this.state.imports)) {
      if (!imp || ['completed', 'failed', 'cancelled', 'timeout'].includes(imp.status)) {
        continue
      }

      const lastMarker = imp.lastServerUpdate || imp.lastUpdated || imp.startTime
      const lastMarkerTime = lastMarker ? new Date(lastMarker).getTime() : NaN
      if (Number.isFinite(lastMarkerTime) && now - lastMarkerTime <= this.stuckImportTimeout) {
        continue
      }

      console.warn('[ImportWS] Import', importId, 'appears idle, validating with backend')

      const status = await importsAPI.getStatus(importId)

      if (!status) {
        console.log('[ImportWS] Backend status unavailable for import', importId)
        continue
      }

      if (status.status && status.status !== 'processing') {
        console.log('[ImportWS] Backend indicates import', importId, 'is', status.status)
        const normalized = {
          status: status.status,
          totalBookmarks:
            status.totalBookmarks ?? status.numberOfBookmarks ?? imp.totalBookmarks ?? 0,
          bookmarksProcessed: status.bookmarksProcessed ?? imp.bookmarksProcessed ?? 0,
          bookmarksFailed: status.bookmarksFailed ?? imp.bookmarksFailed ?? 0,
          progressPercentage: status.progressPercentage ?? imp.progressPercentage ?? 100,
          lastProcessedAt: status.lastProcessedAt ?? status.updatedAt ?? status.completedAt ?? status.scrapeEndedAt,
          completedAt: status.completedAt ?? status.scrapeEndedAt,
          lastError: status.lastError || imp.lastError,
          derived: status.derived,
        }
        this.updateImportProgress(importId, { ...imp, ...normalized }, new Date().toISOString())
        continue
      }

      const backendMarker = status.lastProcessedAt || status.updatedAt || status.startedAt || status.createdAt
      const backendMarkerTime = backendMarker ? new Date(backendMarker).getTime() : NaN

      if (Number.isFinite(backendMarkerTime) && now - backendMarkerTime <= this.stuckImportTimeout) {
        console.log('[ImportWS] Import', importId, 'advanced recently according to backend')
        continue
      }

      this.handleStuckImport(importId, imp)
    }
  }

  handleStuckImport(importId, importData) {
    console.log('[ImportWS] Handling stuck import:', importId)

    const now = new Date()
    const attemptedBase = Number(importData?.attempted)
    const attempted = Number.isFinite(attemptedBase)
      ? attemptedBase
      : Number(importData?.bookmarksProcessed || 0) + Number(importData?.bookmarksFailed || 0)
    const totalBookmarks = Number(importData?.totalBookmarks || 0)
    const pending = Math.max(0, totalBookmarks - attempted)
    const timeoutEntry = {
      ...(importData || {}),
      status: 'timeout',
      attempted,
      pending,
      lastUpdated: now,
      lastServerUpdate: importData?.lastServerUpdate || importData?.lastUpdated || now,
      progressPercentage: importData?.progressPercentage ?? (totalBookmarks > 0 ? Math.round((attempted / totalBookmarks) * 100) : 0),
    }

    this.state.imports[importId] = timeoutEntry

    eventbus.emit('import:completed', {
      importId,
      status: 'timeout',
      totalBookmarks,
      bookmarksProcessed: importData?.bookmarksProcessed,
      bookmarksFailed: importData?.bookmarksFailed || 0,
      progressPercentage: timeoutEntry.progressPercentage,
      attempted,
      pending,
      lastUpdated: timeoutEntry.lastUpdated,
      progress: timeoutEntry,
      error: 'Import timed out - backend may have encountered an issue'
    })

    delete this.state.imports[importId]

    store.dispatch('collectionItems/getStats').catch(console.error)
  }

  sendPing() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'ping' }))
    }
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[ImportWS] Max reconnection attempts reached')
      this.state.error = 'Unable to reconnect to server'
      this.connectionState = 'disconnected'
      eventbus.emit('import:ws:max-reconnect-failed')
      return
    }

    clearTimeout(this.reconnectTimer)

    // Exponential backoff with max delay of 30 seconds
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts), 30000)

    console.log(`[ImportWS] Scheduling reconnection attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts} in ${delay}ms`)

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectAttempts++
      console.log(`[ImportWS] Reconnecting (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`)

      try {
        await this.connect()
        console.log('[ImportWS] Reconnection successful')
      } catch (error) {
        console.error('[ImportWS] Reconnection failed:', error)
        // connect() will handle scheduling the next reconnect attempt if needed
      }
    }, delay)
  }

  handleConnectionFailure() {
    // Emit event to trigger fallback to polling
    eventbus.emit('import:ws:connection-failed')
  }

  disconnect() {
    console.log('[ImportWS] Disconnecting...')
    clearTimeout(this.reconnectTimer)
    this.stopPingInterval()
    this.stopStuckImportDetection()

    if (this.ws) {
      // Remove event handlers to prevent memory leaks
      this.ws.onopen = null
      this.ws.onmessage = null
      this.ws.onerror = null
      this.ws.onclose = null

      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.close(1000, 'Normal closure')
      }
      this.ws = null
    }

    this.state.connected = false
    this.state.connecting = false
    this.state.imports = {}
    this.reconnectAttempts = 0
  }

  // Start tracking a specific import
  trackImport(importId, initialData = {}) {
    console.log('[ImportWS] trackImport called:', {
      importId,
      initialData,
      connected: this.state.connected,
      connecting: this.state.connecting
    })

    // Initialize import in state if not present
    if (!this.state.imports[importId]) {
      const totalBookmarks = Number(initialData.totalBookmarks || 0)
      const processed = Number(initialData.bookmarksProcessed || 0)
      const failed = Number(initialData.bookmarksFailed || 0)
      const attemptedCandidate = Number(initialData.attempted)
      const attempted = Number.isFinite(attemptedCandidate) ? Math.max(0, attemptedCandidate) : processed + failed
      const pendingCandidate = Number(initialData.pending)
      const pending = Number.isFinite(pendingCandidate) ? Math.max(0, pendingCandidate) : Math.max(0, totalBookmarks - attempted)
      const progressCandidate = Number(initialData.progressPercentage)
      const progressPercentage = Number.isFinite(progressCandidate)
        ? Math.min(100, Math.max(0, progressCandidate))
        : totalBookmarks > 0
          ? Math.round((attempted / totalBookmarks) * 100)
          : 0
      const lastMarker =
        initialData.lastServerUpdate ||
        initialData.lastUpdated ||
        initialData.lastProcessedAt ||
        initialData.completedAt ||
        initialData.startTime ||
        initialData.startedAt ||
        new Date()

      this.state.imports[importId] = {
        importId,
        status: initialData.status || 'pending',
        totalBookmarks,
        bookmarksProcessed: processed,
        bookmarksFailed: failed,
        progressPercentage,
        attempted,
        pending,
        startTime: new Date(initialData.startTime || initialData.startedAt || Date.now()),
        lastUpdated: new Date(lastMarker),
        lastServerUpdate: new Date(lastMarker),
        ...initialData
      }
    }

    // Connect if not already connected
    if (!this.state.connected && !this.state.connecting) {
      console.log('[ImportWS] Not connected, initiating connection for import:', importId)
      return this.connect()
    }
    console.log('[ImportWS] Already connected or connecting, import will be tracked:', importId)
    return Promise.resolve(true)
  }

  // Stop tracking a specific import
  stopTracking(importId) {
    console.log('[ImportWS] Stop tracking import:', importId)
    delete this.state.imports[importId]

    // Check if any imports are still active
    const hasActiveImports = Object.keys(this.state.imports).some(id => {
      const imp = this.state.imports[id]
      return imp && !['completed', 'failed', 'cancelled', 'timeout'].includes(imp.status)
    })

    // Disconnect if no more active imports
    if (!hasActiveImports) {
      this.disconnect()
    }
  }

  // Get reactive progress for a specific import
  getImportProgress(importId) {
    return computed(() => this.state.imports[importId] || null)
  }

  // Get all active imports
  getActiveImports() {
    return computed(() => this.state.imports)
  }

  // Check if connected
  isConnected() {
    return computed(() => this.state.connected)
  }

  // Get connection state
  getConnectionState() {
    return computed(() => ({
      connected: this.state.connected,
      connecting: this.state.connecting,
      error: this.state.error
    }))
  }

  // Reset connection (useful for testing or error recovery)
  reset() {
    this.disconnect()
    this.state.error = null
    this.reconnectAttempts = 0
  }
}

// Export singleton instance
export default new ImportProgressWebSocket()
